/**
 * Delegated end-user identity — the WIRE half.
 *
 * A service caller holding the `Agents.Invoke` app role may assert an end-user
 * identity on a conversation-scoped request, so that traffic it relays on behalf
 * of many distinct end users does not collapse onto one conversation. This
 * module owns the two things that must be identical on both sides of the wire:
 * the header name, and the shape a key is allowed to take.
 *
 * What is NOT here, deliberately: how the gateway derives an owner key from an
 * asserted key. That derivation is internal to the gateway (it never appears on
 * the wire), and publishing it would turn an implementation detail into a
 * contract we owe forever.
 *
 * Enforcement is server-side only. These exports are published so a consumer
 * CAN pre-check a key it generates; the gateway validates regardless, and is the
 * only authority.
 */

/**
 * The app role a service principal must hold to call the gateway API at all.
 *
 * One literal, three consumers that must agree or the seam silently breaks: the
 * gateway enforces it, the install path creates it on the app registration, and
 * the published contract declares it. A fresh install whose app registration
 * does not DEFINE this role has a seam that cannot work — no service caller can
 * ever obtain a token carrying it.
 */
export const SERVICE_APP_ROLE = "Agents.Invoke";

/** The request header a service caller sets to assert a delegated end-user identity. */
export const END_USER_KEY_HEADER = "x-m8t-end-user-key";

/** Maximum length of a delegated end-user key. */
export const END_USER_KEY_MAX_LENGTH = 200;

/**
 * The permitted shape of a delegated end-user key: ASCII alphanumerics plus
 * `.`, `_` and `-`, 1–200 characters.
 *
 * Deliberately narrow. The key is an identity ASSERTION by a trusted caller
 * rather than user input, but it still lands in a partition key, so it is
 * bounded by construction. A colon is excluded so the gateway's internal
 * `<prefix>:<principal>:<key>` composition stays unambiguous to read in
 * diagnosis. Anything richer — composite keys, structured ids — the consumer
 * encodes into this alphabet.
 *
 * Built from END_USER_KEY_MAX_LENGTH rather than repeating the bound as a
 * literal, so the documented maximum and the enforced one cannot drift apart.
 */
export const END_USER_KEY_PATTERN = new RegExp(
  `^[A-Za-z0-9._-]{1,${String(END_USER_KEY_MAX_LENGTH)}}$`,
);

/** True when `value` is a string matching {@link END_USER_KEY_PATTERN}. */
export function isValidEndUserKey(value: unknown): value is string {
  return typeof value === "string" && END_USER_KEY_PATTERN.test(value);
}
